import { Button, Dropdown, Form, Input, Select, Space } from "antd";
import { ColumnsType } from "antd/es/table";

import { useEffect, useState } from "react";
import ActionThreeDotIcon from "../../../../assets/icons/ActionThreeDotIcon";
import AddIcon from "../../../../assets/icons/AddIcon";
import EditIcon from "../../../../assets/icons/EditIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import SuccessModal from "../../../../components/common/SuccessModal";
import CustomTable from "../../../../components/ui/CustomTable";
import Loader from "../../../../components/ui/Loader";
import {
  useCreateTerritoryMutation,
  useGetAllAreaQuery,
  useGetAllTerritoryQuery,
  useGetAllZoneQuery,
  useGetSingleTerritoryQuery,
  useUpdateTerritoryMutation,
} from "../../../../features/cashCollection/geoSetup/geoSetupApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import {
  GetAllTerritoryDataParamsType,
  GetAllTerritoryDataType,
} from "../../../../types/cashCollection/geoSetup";
import { countParPageSerialNumber } from "../../../../utils/number";
import { capitalize } from "../../../../utils/string";
import { addKeyInArray } from "../../../../utils/utils";
import AddEditManageModal from "../Modal/AddEditManageModal";

export default function TerritoryList() {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const {
    open: openAddModal,
    handleOpen: handleAddOpen,
    handleClose: handleAddClose,
  } = useModal<undefined, "ADD">();

  const {
    open: openSuccessModal,
    handleOpen: handleSuccessOpen,
    handleClose: handleSuccessClose,
  } = useModal<undefined, "SUCCESS">();

  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    filter,
    handleFilter,
    handleSearch,
    keyword,
  } = useTable<GetAllTerritoryDataParamsType>();

  // Get all Territories
  const { isError, error, data, isLoading } = useGetAllTerritoryQuery({
    params: {
      territoryName: keyword,
      skip: undefined,
      page,
      limit,
    },
  });

  // Get single Territory
  const {
    data: getSingleTerritoryData,
    isError: getSingleTerritoryIsError,
    error: getSingleTerritoryError,
    isLoading: getSingleTerritoryIsLoading,
  } = useGetSingleTerritoryQuery(selectedId || "", {
    skip: !selectedId,
  });

  // Create Territory
  const [
    createTerritory,
    {
      isError: createTerritoryIsError,
      error: createTerritoryError,
      isLoading: createTerritoryIsLoading,
      isSuccess: createTerritoryIsSuccess,
    },
  ] = useCreateTerritoryMutation();

  // Update Territory
  const [
    updateTerritory,
    {
      isError: updateTerritoryIsError,
      error: updateTerritoryError,
      isLoading: updateTerritoryIsLoading,
      isSuccess: updateTerritoryIsSuccess,
    },
  ] = useUpdateTerritoryMutation();

  // Get Zone
  const {
    isError: getAllZoneIsError,
    error: getAllZoneError,
    data: getAllZoneData,
    isLoading: getAllZoneIsLoading,
  } = useGetAllZoneQuery({
    params: {
      page: 1,
      limit: 100000000,
    },
  });

  // Get all Areas
  const {
    isError: getAllAreaIsError,
    error: getAllAreaError,
    data: getAllAreaData,
    isLoading: getAllAreaIsLoading,
  } = useGetAllAreaQuery(
    {
      params: {
        page: 1,
        limit: 100000000,
        zoneId: selectedZoneId ? selectedZoneId : undefined,
      },
    },
    {
      skip: !selectedZoneId,
    }
  );

  // 🔹 Centralized error handling
  useError(isError, error, "Get all territory fail");
  useError(
    getSingleTerritoryIsError,
    getSingleTerritoryError,
    "Get single territory fail"
  );
  useError(
    createTerritoryIsError,
    createTerritoryError,
    "Create territory fail"
  );
  useError(
    updateTerritoryIsError,
    updateTerritoryError,
    "Update territory fail"
  );
  useError(getAllAreaIsError, getAllAreaError, "Get all area fail");
  useError(getAllZoneIsError, getAllZoneError, "Get all zone fail");

  const onFinish = (values: any) => {
    const newData = {
      ...values,
      active: true,
    };
    if (selectedId) {
      updateTerritory({
        id: selectedId,
        data: values,
      });
    } else {
      createTerritory(newData);
    }
  };

  const getModalTitle = () => {
    if (openAddModal.type === "ADD" && selectedId) {
      return "Territory Successfully Updated!";
    }
    if (openAddModal.type === "ADD") {
      return "Territory Successfully Added!";
    }
    return "";
  };

  const handleOkay = () => {
    handleCloseModal();
    handleSuccessClose();
  };

  const handleCloseModal = () => {
    setSelectedId(null);
    setSelectedZoneId(null);
    form.setFieldsValue({});
    form.resetFields();
    handleAddClose();
  };

  useEffect(() => {
    if (getSingleTerritoryData?.data && selectedId) {
      form.setFieldsValue({
        zoneId: getSingleTerritoryData?.data?.zone?.id,
        areaId: getSingleTerritoryData?.data?.area?.id,
        territoryName: capitalize(getSingleTerritoryData?.data?.territoryName),
      });

      setSelectedZoneId(getSingleTerritoryData?.data?.zone?.id);
    }
  }, [getSingleTerritoryData?.data, selectedId, setSelectedZoneId, form]);

  useEffect(() => {
    if (createTerritoryIsSuccess) {
      handleSuccessOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createTerritoryIsSuccess]);

  useEffect(() => {
    if (updateTerritoryIsSuccess) {
      handleSuccessOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateTerritoryIsSuccess]);

  const columns: ColumnsType<GetAllTerritoryDataType> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },

    {
      title: <div className="text-nowrap">Zone</div>,
      dataIndex: "zone",
      align: "center",
      render: (val) => {
        return capitalize(val?.zoneName) || "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Area</div>,
      dataIndex: "area",
      align: "center",
      render: (val) => {
        return capitalize(val?.areaName) || "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Territory</div>,
      dataIndex: "territoryName",
      align: "center",
      render: (val) => {
        return capitalize(val) || "N/A";
      },
    },
    // {
    //   title: <div className="text-nowrap">Created at</div>,
    //   dataIndex: "remaining",
    //   align: "center",
    //   render: (val) => {
    //     return val || "N/A";
    //   },
    // },
    {
      title: <div className="whitespace-nowrap">Status</div>,
      dataIndex: "active",
      align: "center",
      render: (val: boolean) => {
        const status = val ? "Active" : "Inactive";

        const statusClasses: Record<string, string> = {
          Active: "bg-green-100 text-green-700",
          Inactive: "bg-red-100 text-red-700",
        };

        return (
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${statusClasses[status]}`}
          >
            {status}
          </span>
        );
      },
    },

    {
      title: "Actions",
      dataIndex: "id",
      align: "center",
      render: (val: number, record: any) => {
        const items = [
          // {
          //   key: "active",
          //   label: (
          //     <div className="flex justify-between items-center pb-2 border-b border-[#D7DFE9]">
          //       <span className="text-gray-700">Active</span>
          //       <Switch defaultChecked size="small" />
          //     </div>
          //   ),
          // },
          {
            key: "edit",
            label: (
              <div
                className="flex items-center gap-2 justify-start text-slate-700 text-sm font-medium"
                onClick={() => {
                  setSelectedId(val);
                  handleAddOpen({ type: "ADD" });
                }}
              >
                <EditIcon />
                <span>Edit</span>
              </div>
            ),
          },
        ];

        return (
          <div className="flex justify-center items-center gap-2">
            <Dropdown
              menu={{
                items,
                className: "min-w-[170px] !h-[40px] ",
              }}
              trigger={["click"]}
              placement="bottomRight"
              arrow={{ pointAtCenter: true }}
            >
              <Button
                className="action-button disabled:!bg-[#ddd] !border-none"
                size="small"
                icon={<ActionThreeDotIcon />}
              />
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <SidebarLayout title="GEO Set-up">
        <CustomTable
          loading={isLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={data?.data?.count}
          dataSource={addKeyInArray(data?.data?.rows)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          filterSection={
            <>
              <Input
                suffix={<SearchIcon />}
                placeholder="Search Territory"
                style={{ width: 215 }}
                allowClear
                onChange={handleSearch}
              />

              <Button
                type="primary"
                className="middle-button"
                icon={<AddIcon />}
                onClick={() => handleAddOpen({ type: "ADD" })}
              >
                New Territory
              </Button>
            </>
          }
        />
      </SidebarLayout>

      {openAddModal?.type === "ADD" && (
        <AddEditManageModal
          open={openAddModal?.open}
          handleClose={handleCloseModal}
        >
          <div>
            <div className="justify-start text-zinc-700 text-xl font-medium">
              {selectedId ? "Update Territory" : "Add New Territory"}
            </div>

            {getSingleTerritoryIsLoading ||
            getAllZoneIsLoading ||
            getAllAreaIsLoading ? (
              <Loader />
            ) : (
              <div className="mt-5">
                <Form
                  className="custom-form"
                  layout="vertical"
                  requiredMark="optional"
                  size="large"
                  onFinish={onFinish}
                  form={form}
                >
                  <Form.Item
                    label="Zone"
                    name="zoneId"
                    rules={[{ required: true, message: "Please enter zone" }]}
                  >
                    <Select
                      placeholder="Select Zone"
                      suffixIcon={<SelectDownIcon />}
                      showSearch
                      allowClear
                      className="w-full"
                      disabled={!!selectedId}
                      options={
                        getAllZoneData?.data?.rows?.map((val: any) => ({
                          label: capitalize(val?.zoneName),
                          value: val?.id,
                        })) || []
                      }
                      onChange={(value) => setSelectedZoneId(value)}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Area"
                    name="areaId"
                    rules={[{ required: true, message: "Please enter area" }]}
                  >
                    <Select
                      placeholder="Select Area"
                      suffixIcon={<SelectDownIcon />}
                      showSearch
                      allowClear
                      className="w-full"
                      disabled={!selectedZoneId || !!selectedId}
                      options={
                        getAllAreaData?.data?.rows?.map((val: any) => ({
                          label: capitalize(val?.areaName),
                          value: val?.id,
                        })) || []
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    label="Territory Name"
                    name="territoryName"
                    rules={[
                      {
                        required: true,
                        message: "Please enter territory name",
                      },
                    ]}
                  >
                    <Input placeholder="Enter Territory Name" />
                  </Form.Item>

                  <Space className="w-full mt-5" size={12}>
                    <Button
                      className="normal-dark-button modal-button !h-[40px]"
                      onClick={handleCloseModal}
                      size="middle"
                    >
                      Cancel
                    </Button>

                    <Button
                      className="modal-button !h-[40px]"
                      type="primary"
                      size="middle"
                      htmlType="submit"
                      loading={
                        createTerritoryIsLoading || updateTerritoryIsLoading
                      }
                    >
                      {selectedId ? "Update Territory" : "Add Territory"}
                    </Button>
                  </Space>
                </Form>
              </div>
            )}
          </div>
        </AddEditManageModal>
      )}

      {openSuccessModal.type === "SUCCESS" && (
        <SuccessModal
          open={openSuccessModal?.open}
          handleClose={handleSuccessClose}
          handleOkay={handleOkay}
          data={openSuccessModal?.data}
          title={getModalTitle()}
        />
      )}
    </>
  );
}
